import { FC, useState } from 'react';

import { ButtonVariant, Button } from '@pp/core/src/components/controls/button/Button';
import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';
import { getIsLanguageRTL } from '@pp/core/src/utils/language';

import { useLastGameDataFetcher } from '../../../../store/apiHooks/useLastGameDataFetcher';
import { useHighflyerGameStateSlice } from '../../../../store/slices/highflyerGame/selectors';
import { useCurrencySymbol } from '../../../gameGrid/hooks';

import { SingleBetRow } from './SingleBetRow';

import styles from './BetsHistoryTable.module.css';
import { SingleBetHistoryType } from '../../../../store/slices/highflyerGame/types';

interface BetsHistoryTableProps {
  show: boolean;
  showStaticData: boolean;
}

export const BetHistoryTable: FC<BetsHistoryTableProps> = ({ show, showStaticData }) => {
  const [page, setPage] = useState(1);
  const audioTrigger = useAudioTrigger();
  const currencySymbol = useCurrencySymbol();
  const locale = useSettingsSlice.currentLanguage();
  const isLanguageRTL = showStaticData? false : getIsLanguageRTL(locale);
  const lastGameDataFetcher = useLastGameDataFetcher();
  const currency = useGameStateSlice.getCurrency() ?? '';
  const currentLanguage = useSettingsSlice.currentLanguage();
  const lastGameData = useHighflyerGameStateSlice.lastGameData();
  const isLoading = useHighflyerGameStateSlice.getIsLoadingPreviousGameData();
  const isLoadMore = useHighflyerGameStateSlice.getIsLoadMorePreviousGameData();
  const _show = showStaticData ? true : show;
  const staticData: SingleBetHistoryType[] = [
      {
        betAmt: 89,
        betAmount: 89,
        betTime: "2024-01-01T00:00:00Z",
        cashOutMultiplier: 0,
        nickname: "",
        userId: "ppc1735169885568",
        winAmount: 0,
        cCode: "TRY",
        sName: "Asami",
        uId: "ppc1735169885568",
        mul: -1,
        winAmt: 0,
    },
    {
      betAmt: 70,
      betAmount: 70,
      betTime: "2024-01-01T00:00:00Z",
      cashOutMultiplier: 2.01,
      nickname: "",
      userId: "ppc1735169885568",
      winAmount: 140.7,
      cCode: "TRY",
      sName: "Junior",
      uId: "ppc1735169885568",
      mul: 2.01,
      winAmt: 140.7,
  },
  {
    betAmt: 50,
    betAmount: 50,
    betTime: "2024-01-01T00:00:00Z",
    cashOutMultiplier: 0,
    nickname: "",
    userId: "ppc1735169885568",
    winAmount: 0,
    cCode: "TRY",
    sName: "Spicy",
    uId: "ppc1735169885568",
    mul: -1,
    winAmt: 0,
  },
  {
    betAmt: 40,
    betAmount: 40,
    betTime: "2024-01-01T00:00:00Z",
    cashOutMultiplier: 0,
    nickname: "",
    userId: "ppc1735169885568",
    winAmount: 0,
    cCode: "TRY",
    sName: "Master",
    uId: "ppc1735169885568",
    mul: -1,
    winAmt: 0,
  },
  {
    betAmt: 20,
    betAmount: 20,
    betTime: "2024-01-01T00:00:00Z",
    cashOutMultiplier: 2.01,
    nickname: "",
    userId: "ppc1735169885568",
    winAmount: 0,
    cCode: "TRY",
    sName: "Creonix",
    uId: "ppc1735169885568",
    mul: 2.01,
    winAmt: 40.2,
  },
  {
    betAmt: 10,
    betAmount: 10,
    betTime: "2024-01-01T00:00:00Z",
    cashOutMultiplier: 2.01,
    nickname: "",
    userId: "ppc1735169885568",
    winAmount: 0,
    cCode: "TRY",
    sName: "NewPlayer",
    uId: "ppc1735169885568",
    mul: 2.01,
    winAmt: 20.1,
  },
    {
        betAmt: 10,
        betAmount: 10,
        betTime: "2024-01-01T00:00:00Z",
        cashOutMultiplier: 0,
        nickname: "Massimo",
        userId: "ppc1735169885568",
        winAmount: 0,
        cCode: "TRY",
        sName: "Massimo",
        uId: "ppc1735169885568",
        mul: -1,
        winAmt: 0,
    },
];
  const currentGameData = showStaticData ? staticData : useHighflyerGameStateSlice.leaderBoardHistory()?.user;

  const mods = useModifiers(
    styles,
    'betHistory_header_row',
    {
      small: currency === 'mBTC' || ['bn', 'hi', 'ar'].includes(currentLanguage),
      rtl: isLanguageRTL,
    },
    { withBaseClass: true }
  );
  const staticMods = useModifiers(
    styles,
    'betHistory_table',
    {
      static: showStaticData,
    },
    { withBaseClass: true }
  );

  return (
    <>
      <table className={staticMods}>
        <div className={styles.header_border}></div>
        <thead className={styles.betHistory_header}>
          <tr className={mods}>
            <td className={styles.firstCol}>
              {showStaticData ? 'User' : <L10nText tk="USER" />}
            </td>
            <td className={styles.secondCol}>
              {showStaticData ? 'Bet' : <L10nText tk="BETLABEL" />}
              <span>&nbsp;{currencySymbol}</span>
            </td>
            <td className={styles.thirdCol}></td>
            <td className={styles.fourthCol}>
              {showStaticData ? 'Cashed' : <L10nText tk="CASHED" />}
              <span>&nbsp;{currencySymbol}</span>
            </td>
          </tr>
        </thead>
        <tbody className={styles.betHistory_body}>
          <SingleBetRow data={!_show ? lastGameData?.players : currentGameData} show={_show} />
        </tbody>
      </table>
      {lastGameData?.players && lastGameData.players.length > 0 && !_show && isLoadMore && (
        <div className={styles.loadMore}>
          <Button
            tk="LOAD_MORE"
            variant={ButtonVariant.MAIN}
            disabled={!isLoadMore}
            loading={!isLoading}
            onClick={() => {
              setPage((prev) => prev + 1);
              audioTrigger.play({ name: 'menuClick' });
              lastGameDataFetcher(true, page + 1, lastGameData?.gameId);
            }}
            className={styles.buttonBg}
          ></Button>
        </div>
      )}
    </>
  );
};
